import { CreditPackage, GameType } from "./types";

/** Chuẩn hóa AdSense Client ID: chấp nhận "ca-pub-XXX" hoặc "pub-XXX" (tự thêm ca-) */
export const normalizeAdSenseClientId = (val: string | null | undefined): string => {
    if (!val || typeof val !== 'string') return '';
    const t = val.trim();
    if (t.startsWith('ca-pub-')) return t;
    if (t.startsWith('pub-')) return 'ca-' + t;
    return t;
};

// =================================================================================
// CẤU HÌNH MÔI TRƯỜNG & API
// =================================================================================
export const APP_MODE: 'DEVELOPMENT' | 'PRODUCTION' = 'PRODUCTION';

// Backend API Configuration
export const API_BASE_URL = window.location.origin; // Dùng origin hiện tại để tận dụng Vite Proxy trong môi trường dev
export const API_EDU_PREFIX = '/api/edu';

/** CDN chỉ cho /storage, /uploads. Legacy: VITE_ASSET_BASE_URL. Để trống → cùng origin (dev: Vite proxy; prod: có thể proxy /storage trên nginx). */
export const MEDIA_CDN_BASE_URL =
  (import.meta.env.VITE_MEDIA_CDN_URL as string) || (import.meta.env.VITE_ASSET_BASE_URL as string) || '';

/**
 * URL public của backend (ví dụ https://be.bookai.vn). Bắt buộc khi VITE_API_BASE_URL trống (API qua proxy) mà cần mở Filament/admin.
 * Dev: có thể trùng VITE_API_BASE_URL (http://127.0.0.1:8001).
 */
export const BACKEND_PUBLIC_URL =
  (import.meta.env.VITE_BACKEND_PUBLIC_URL as string) || (import.meta.env.VITE_API_BASE_URL as string) || '';

const trimTrailingSlash = (s: string) => s.replace(/\/$/, '');

/**
 * /storage và /uploads → MEDIA_CDN (nếu cấu hình); còn lại (logo, /assets/…) → origin SPA.
 */
/** True when the string looks like a stored asset path/URL, not an AI image description. */
export function isLikelyAssetUrl(value: string | undefined | null): boolean {
  if (value == null) return false;
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith('http://') || v.startsWith('https://') || v.startsWith('data:')) return true;
  if (v.startsWith('/storage/') || v.startsWith('/uploads/') || v.startsWith('/assets/')) return true;
  if (v.startsWith('storage/') || v.startsWith('uploads/')) return true;
  return /\.(png|jpe?g|gif|webp|svg|bmp|ico)(\?.*)?$/i.test(v);
}

export function resolvePublicFileUrl(path: string | undefined | null): string {
  if (path == null || path === '') return '';
  if (path.startsWith('http') || path.startsWith('data:')) return path;

  let normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized.startsWith('/uploads/')) {
    normalized = normalized.replace('/uploads/', '/storage/');
  }

  if (normalized.startsWith('/storage/')) {
    const base = trimTrailingSlash(MEDIA_CDN_BASE_URL);
    if (base) return `${base}${normalized}`;
    return normalized;
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${normalized}`;
}

/**
 * Một mục trong JSON homepage_teacher_tools_media / homepage_student_tools_media:
 * - Chuỗi mảng URL (legacy), hoặc
 * - { urls: string[], title?: string, description?: string }
 */
export function getHomepageToolMediaEntry(
  map: Record<string, unknown> | undefined,
  view: string,
): { urls?: string[]; title?: string; description?: string } {
  if (!map || !Object.prototype.hasOwnProperty.call(map, view)) {
    return {};
  }
  const raw = map[view];
  if (Array.isArray(raw)) {
    const urls = raw
      .filter((u): u is string => typeof u === 'string' && u.trim().length > 0)
      .map((u) => u.trim());
    return { urls };
  }
  if (typeof raw === 'object' && raw !== null) {
    const o = raw as Record<string, unknown>;
    const urls = Array.isArray(o.urls)
      ? o.urls
          .filter((u): u is string => typeof u === 'string' && u.trim().length > 0)
          .map((u) => u.trim())
      : [];
    const title = typeof o.title === 'string' && o.title.trim() ? o.title.trim() : undefined;
    const description =
      typeof o.description === 'string' && o.description.trim() ? o.description.trim() : undefined;
    return { urls, title, description };
  }
  return { urls: [] };
}

export const API_CONFIG = {
    BASE_URL: API_BASE_URL,
    PREFIX: API_EDU_PREFIX,
    TIMEOUT: 180000, // 3 minutes
    ENABLE_AI_SELECTOR: false,
    DEFAULT_PROVIDERS: {
        LESSON_PLAN: 'gemini' as any,
        QUIZ: 'gemini' as any,
        GAME: 'gemini' as any,
        PRESENTATION: 'gemini' as any,
        SLIDES: 'gemini' as any,
        HOMEWORK: 'gemini' as any,
        ACTIVITY: 'gemini' as any,
        IMAGE: 'gemini' as any,
        VIDEO: 'gemini' as any,
    },
    GEMINI_TEXT_MODEL: 'gemini-1.5-flash',
    /** Đồng bộ với backend `ai_model_html5` (GenerateService::getHtml5GenerationModel). Mặc định: gemini-3-flash-preview. */
    GEMINI_HTML5_MODEL: 'gemini-3-flash-preview',
    OPENAI_TEXT_MODEL: 'gpt-4o-mini',
    OPENAI_MODEL_IMAGE: 'dall-e-3',
    /** Đồng bộ với backend `eduapp::ai.anthropic.models.text` & setting `anthropic_model_text`. */
    ANTHROPIC_TEXT_MODEL: 'claude-sonnet-4-5',
    /**
     * Bảng định giá thông minh theo cặp provider × model. Sync từ backend
     * (config/credit_pricing.php + setting JSON `ai_model_pricing`) qua
     * configService.applyToGlobals(). Cấu trúc:
     *   { [provider]: { [model]: { multiplier: number; tokens_per_credit: number } } }
     * Có entry `_default` dự phòng. Dùng cho UI hiển thị credit ước tính.
     */
    MODEL_PRICING: {} as Record<string, Record<string, { multiplier: number; tokens_per_credit: number }>>,
    PIPELINE_ROUTING: {} as PipelineRoutingMatrix,
    /**
     * Từng nhà CC hiển thị trong bộ chọn model (toggle admin ∧ có API key). Sync backend `ai_provider_selectable`.
     */
    AI_PROVIDER_SELECTABLE: {
        gemini: false,
        openai: false,
        anthropic: false,
    } as Record<'gemini' | 'openai' | 'anthropic', boolean>,
};

export type AIProvider = 'gemini' | 'openai' | 'anthropic';

export type PipelineStage = 'data' | 'image' | 'image_understanding' | 'html5' | 'video';
export type PipelineTier = 'basic' | 'premium' | 'advanced';
export type PipelineFeature = 'global' | 'presentation' | 'simulation' | 'thiet_ke_3d' | 'lesson_plan' | 'homework' | 'game';
export type PipelineRoute = { provider: AIProvider; model: string };
export type PipelineRoutingMatrix = Partial<Record<PipelineFeature, Partial<Record<PipelineStage, Partial<Record<PipelineTier, PipelineRoute>>>>>>;

// --- CHI PHÍ CREDIT (Khớp với Backend) ---
export const CreditCost = {
    LESSON_PLAN: 2,
    PRESENTATION: 3,
    QUIZ: 1,
    HOMEWORK: 1,
    GAME: 1,
    ACTIVITY: 1,
    CHILD_PLAN: 1,
    PRONUNCIATION: 1,
    DESIGN: 2,
    IMAGE: 2,
    VIDEO: 5,
    VIDEO_PROJECT: 1,
    SEGMENTATION: 1,
    SAMPLE_DATA: 1,
    SCIENTIFIC_SIMULATION_DATA: 1,
    SCIENTIFIC_SIMULATION_HTML5: 6,
    THIET_KE_3D_DESC: 1,
    THIET_KE_3D_DATA: 2,
    /** HTML5 thiết kế 3D — khớp mặc định `cost_thiet_ke_3d_html5` / slidesUpgradeHtml5 */
    THIET_KE_3D_HTML5: 6,
    /** AI tạo từng phần SKKN (part1–part7) — khớp eduapp::costs.SKKN_SECTION */
    SKKN_SECTION: 1,
    MUSIC_QUICK: 3,
    MUSIC_ENTERPRISE: 30,
};

// =================================================================================
// CẤU HÌNH MẶC ĐỊNH & MOCK DATA
// =================================================================================
export const INITIAL_USER_SETTINGS: any = {
    teacherName: 'Người dùng mới',
    schoolName: '',
    className: '',
    phoneNumber: '',
    email: '',
    avatar: null,
    defaultGradeLevel: 'Tiểu học' as any,
    mainSubject: 'Khoa học',
    defaultTeachingDuration: 45,
    totalCredits: 0, 
    usedCredits: 0,
    distributedCredits: 0,
    distributedStorageMB: 0,
    storageLimitMB: 1024,
    storageUsedMB: 0,
    groupStorageLimitMB: 0,
    groupStorageUsedMB: 0,
    role: 'teacher',
    authType: 'standard',
    quickMenuItems: [],
    showFloatingMenu: true,
    adsEnabled: true,
    creditLog: [],
    creditShareLog: [],
    footerSettings: [
        { key: 'teacherName', label: 'Tên giáo viên', include: true, alignment: 'left' },
        { key: 'schoolName', label: 'Tên trường', include: true, alignment: 'left' },
        { key: 'className', label: 'Tên lớp', include: false, alignment: 'left' },
        { key: 'phoneNumber', label: 'Số điện thoại', include: false, alignment: 'right' },
        { key: 'email', label: 'Email', include: false, alignment: 'right' },
    ],
};

export const MOCK_USER_CREDENTIALS = {
    teacher: { username: 'teacher@bookai.vn', password: 'password' },
    parent: { username: 'parent@bookai.vn', password: 'password' },
    student: { username: 'student@bookai.vn', password: 'password' },
};

// =================================================================================
// CẤU HÌNH GÓI TÍN DỤNG (HYBRID MODEL)
// =================================================================================
export const CREDIT_PACKAGES: CreditPackage[] = [
  // --- Gói Đăng ký Cá nhân (Tháng) ---
  {
    id: 'sub_personal_basic_monthly',
    category: 'subscription_personal_monthly',
    billingCycle: 'monthly',
    name: 'Gói Cơ bản',
    credits: 150,
    priceVND: 99000,
    originalPriceVND: 129000,
    aiTier: 'basic',
    maxClasses: 5,
    maxStudents: 100,
    maxGroups: 2,
    maxGroupMembers: 10,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    maxChildren: 2,
    maxLearningPlans: 10,
    tagline: 'Giáo viên dùng nhẹ, lớp nhỏ',
    description: '',
    isPremiumEligible: false,
    canRemoveWatermark: false,
    features: [
      '≈ <strong>75 giáo án</strong> · <strong>50 slide</strong> · <strong>150 đề kiểm tra</strong> mỗi tháng.',
      'Chat AI thoải mái mỗi ngày (giới hạn mềm).',
      'Bảo lưu tối đa <strong>50%</strong> khả năng AI dư.',
      'Dung lượng lưu trữ: <strong>2 GB</strong>',
      'Tạo tối đa <strong>2 nhóm</strong> với tổng <strong>10 thành viên</strong> để cộng tác học liệu.',
      'Có dấu mộc "Powered by Bookai.vn".',
      'AI Tiêu chuẩn — nhanh & tiết kiệm cho công việc hằng ngày.'
    ]
  },
  {
    id: 'sub_personal_pro_monthly',
    category: 'subscription_personal_monthly',
    billingCycle: 'monthly',
    name: 'Gói Chuyên nghiệp',
    credits: 350,
    priceVND: 199000,
    originalPriceVND: 249000,
    aiTier: 'premium',
    maxClasses: 15,
    maxStudents: 350,
    maxGroups: 4,
    maxGroupMembers: 20,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    maxChildren: 5,
    maxLearningPlans: 50,
    tagline: 'Giáo viên dùng AI hằng ngày',
    description: '',
    isPopular: true,
    isPremiumEligible: false,
    canRemoveWatermark: false,
    features: [
      '≈ <strong>115 giáo án</strong> · <strong>70 slide</strong> · <strong>230 đề kiểm tra</strong> mỗi tháng.',
      'Chat AI gần như không giới hạn (200 tin nhắn/ngày).',
      'Bảo lưu tối đa <strong>50%</strong> khả năng AI dư.',
      'Dung lượng lưu trữ: <strong>10 GB</strong>',
      'Mở tối đa <strong>4 nhóm</strong> với tổng <strong>20 thành viên</strong> để cộng tác học liệu.',
      'Chưa gồm chia credit và phân bổ credit tháng.',
      'Có dấu mộc "Powered by Bookai.vn".',
      'AI Cao cấp — sâu & chất lượng cao cho nội dung chuyên môn.'
    ]
  },
  {
    id: 'sub_personal_advanced_monthly',
    category: 'subscription_personal_monthly',
    billingCycle: 'monthly',
    name: 'Gói Nâng cao',
    credits: 800,
    priceVND: 399000,
    originalPriceVND: 499000,
    aiTier: 'advanced',
    maxClasses: 50,
    maxStudents: 1000,
    maxGroups: 8,
    maxGroupMembers: 40,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    maxChildren: 10,
    maxLearningPlans: -1,
    tagline: 'Trung tâm nhỏ / GV chuyên sâu AI-heavy',
    description: '',
    isPremiumEligible: false,
    canRemoveWatermark: false,
    features: [
      '≈ <strong>200 giáo án</strong> · <strong>135 slide</strong> · <strong>400 đề kiểm tra</strong> mỗi tháng.',
      'Chat AI gần như không giới hạn (500 tin nhắn/ngày).',
      'Bảo lưu tối đa <strong>50%</strong> khả năng AI dư.',
      'Dung lượng lưu trữ: <strong>30 GB</strong>',
      'Mở tối đa <strong>8 nhóm</strong> với tổng <strong>40 thành viên</strong> để cộng tác học liệu.',
      'Chưa gồm chia credit và phân bổ credit tháng.',
      'Có dấu mộc "Powered by Bookai.vn".',
      'AI Cao cấp Pro — ưu tiên hàng đợi xử lý cao nhất.'
    ]
  },

  // --- Gói Đăng ký Cá nhân (Năm) ---
  {
    id: 'sub_personal_basic_annual',
    category: 'subscription_personal_annual',
    billingCycle: 'annually',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói Cơ bản (Năm)',
    credits: 1800, 
    priceVND: 990000, 
    originalPriceVND: 1188000, 
    aiTier: 'basic',
    maxClasses: 8,
    maxStudents: 150,
    maxGroups: 3,
    maxGroupMembers: 15,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    maxChildren: 3,
    maxLearningPlans: 20,
    tagline: 'Tiết kiệm 3 tháng — gói năm',
    description: '',
    features: [
      '≈ <strong>75 giáo án</strong> · <strong>50 slide</strong> · <strong>150 đề kiểm tra</strong> mỗi tháng.',
      'Chat AI thoải mái mỗi ngày — Bảo lưu <strong>100%</strong> khả năng AI dư.',
      'Dung lượng lưu trữ: <strong>5 GB</strong>',
      'Mở tối đa <strong>3 nhóm</strong> với tổng <strong>15 thành viên</strong> để cùng chia sẻ học liệu.',
      'Chưa gồm chia credit và phân bổ credit tháng.',
      'Nhãn <strong>Premium</strong> & Ưu tiên hỗ trợ.',
      'Xóa dấu mộc hệ thống & Logo riêng.',
      'Affiliate: Nhận hoa hồng <strong>Tiền mặt</strong>.'
    ]
  },
  {
    id: 'sub_personal_pro_annual',
    category: 'subscription_personal_annual',
    billingCycle: 'annually',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói Chuyên nghiệp (Năm)',
    credits: 4200, 
    priceVND: 1990000, 
    originalPriceVND: 2388000, 
    aiTier: 'premium',
    maxClasses: 25,
    maxStudents: 500,
    maxGroups: 6,
    maxGroupMembers: 30,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    maxChildren: 8,
    maxLearningPlans: 100,
    tagline: 'Tiết kiệm 3 tháng — gói năm',
    description: '',
    features: [
      '≈ <strong>115 giáo án</strong> · <strong>70 slide</strong> · <strong>230 đề kiểm tra</strong> mỗi tháng.',
      'Chat AI gần như không giới hạn — Bảo lưu <strong>100%</strong> khả năng AI dư.',
      'Dung lượng lưu trữ: <strong>15 GB</strong>',
      'Mở tối đa <strong>6 nhóm</strong> với tổng <strong>30 thành viên</strong> để cùng chia sẻ học liệu.',
      'Phù hợp tổ nhỏ, nhóm giáo viên hoặc nhóm phụ huynh.',
      'Chưa gồm chia credit và phân bổ credit tháng.',
      'Nhãn <strong>Premium</strong> & Ưu tiên hỗ trợ.',
      'Xóa dấu mộc hệ thống & Logo riêng.',
      'Affiliate: Nhận hoa hồng <strong>Tiền mặt</strong>.'
    ]
  },
  {
    id: 'sub_personal_advanced_annual',
    category: 'subscription_personal_annual',
    billingCycle: 'annually',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói Nâng cao (Năm)',
    credits: 9600, 
    priceVND: 3990000, 
    originalPriceVND: 4788000, 
    aiTier: 'advanced',
    maxClasses: 100,
    maxStudents: 2000,
    maxGroups: 12,
    maxGroupMembers: 60,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    maxChildren: 20,
    maxLearningPlans: -1,
    tagline: 'Tiết kiệm 3 tháng — gói năm',
    description: '',
    features: [
      '≈ <strong>200 giáo án</strong> · <strong>135 slide</strong> · <strong>400 đề kiểm tra</strong> mỗi tháng.',
      'Chat AI gần như không giới hạn — Bảo lưu <strong>100%</strong> khả năng AI dư.',
      'Dung lượng lưu trữ: <strong>50 GB</strong>',
      'Mở tối đa <strong>12 nhóm</strong> với tổng <strong>60 thành viên</strong> để cùng chia sẻ học liệu.',
      'Chưa gồm chia credit và phân bổ credit tháng.',
      'Nhãn <strong>Premium</strong> & Ưu tiên hỗ trợ.',
      'Xóa dấu mộc hệ thống & Logo riêng.',
      'Affiliate: Nhận hoa hồng <strong>Tiền mặt</strong>.'
    ]
  },

  // --- Gói Nhóm, Tổ chức & Trường học ---
  {
    id: 'group_team_annual',
    category: 'group_annual',
    billingCycle: 'annually',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói Tổ/Nhóm',
    credits: 25000,
    priceVND: 9999000,
    originalPriceVND: 11999000,
    aiTier: 'advanced',
    maxClasses: 200,
    maxStudents: 5000,
    maxGroups: 20,
    maxGroupMembers: 200,
    canShareCredits: true,
    canAllocateGroupMonthly: true,
    tagline: 'Tổ chuyên môn 10+ giáo viên',
    description: '',
    isPopular: true,
    features: [
        '≈ <strong>520 giáo án</strong> · <strong>350 slide</strong> · <strong>1.000 đề kiểm tra</strong> mỗi tháng cho cả nhóm.',
        'Chat AI gần như không giới hạn cho mọi thành viên.',
        'Chia credit thủ công + phân bổ ngân sách AI theo tháng cho từng thành viên.',
        'Nhãn <strong>Premium</strong> toàn hệ thống.',
        'Xóa dấu mộc hệ thống & Logo riêng/Trường.',
        'Mở tối đa <strong>20 nhóm</strong> với tổng <strong>200 thành viên</strong>.',
        'Affiliate: Nhận hoa hồng <strong>Tiền mặt</strong>.'
    ]
  },
  {
    id: 'group_org_annual',
    category: 'group_annual',
    billingCycle: 'annually',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói Tổ chức',
    credits: 70000,
    priceVND: 19999000,
    originalPriceVND: 24999000,
    aiTier: 'advanced',
    maxClasses: 1000,
    maxStudents: 25000,
    maxGroups: 50,
    maxGroupMembers: 1000,
    canShareCredits: true,
    canAllocateGroupMonthly: true,
    tagline: 'Doanh nghiệp & trung tâm 25+ giáo viên',
    description: '',
    features: [
        '≈ <strong>1.450 giáo án</strong> · <strong>970 slide</strong> · <strong>2.900 đề kiểm tra</strong> mỗi tháng cho cả tổ chức.',
        'Chat AI gần như không giới hạn cho mọi thành viên.',
        'Quản trị credit tập trung — phân bổ ngân sách theo nhiều phòng ban.',
        'Nhãn <strong>Premium</strong> toàn hệ thống.',
        'Xóa dấu mộc hệ thống & Logo riêng/Trường.',
        'Mở tối đa <strong>50 nhóm</strong> với tổng <strong>1.000 thành viên</strong>.',
        'Affiliate: Nhận hoa hồng <strong>Tiền mặt</strong>.'
    ]
  },
  {
    id: 'group_school_contact',
    category: 'group_annual',
    billingCycle: 'contact',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói Trường học',
    credits: -1,
    priceVND: -1,
    maxClasses: -1,
    maxStudents: -1,
    maxGroups: -1,
    maxGroupMembers: -1,
    canShareCredits: true,
    canAllocateGroupMonthly: true,
    tagline: 'Triển khai riêng cho trường học và hệ thống lớn',
    description: '',
    features: [
        '<strong>Tùy chỉnh</strong> số lượng credits theo nhu cầu.',
        'Dung lượng lưu trữ <strong>không giới hạn</strong>.',
        'Số nhóm và thành viên <strong>không giới hạn</strong> theo phương án triển khai.',
        'Nhãn <strong>Premium</strong> & Hỗ trợ chuyên sâu.'
    ]
  },
  // --- Gói Booster (Mua lẻ tiết kiệm) ---
  {
    id: 'booster_s',
    category: 'onetime',
    billingCycle: 'onetime',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói AI Mở rộng',
    credits: 1000,
    priceVND: 800000,
    originalPriceVND: 1000000,
    aiTier: 'premium',
    maxGroups: 0,
    maxGroupMembers: 0,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    tagline: 'Khi cần "nạp thêm" cho mùa cao điểm — Tiết kiệm 20%',
    description: '',
    features: [
      '≈ <strong>330 giáo án</strong> · <strong>200 slide</strong> · <strong>660 đề kiểm tra</strong> dùng linh hoạt.',
      'Nhãn <strong>Premium</strong> & Xóa dấu mộc.',
      'Không giới hạn thời gian sử dụng.',
      'Không mở thêm quyền nhóm hoặc chia credit.',
      'Xử lý AI tức thì, không quảng cáo.'
    ]
  },
  {
    id: 'booster_m',
    category: 'onetime',
    billingCycle: 'onetime',
    isPremiumEligible: true,
    canRemoveWatermark: true,
    name: 'Gói AI Pro Pack',
    credits: 5250, 
    priceVND: 3500000,
    originalPriceVND: 5000000,
    aiTier: 'advanced',
    maxGroups: 0,
    maxGroupMembers: 0,
    canShareCredits: false,
    canAllocateGroupMonthly: false,
    tagline: 'Power user / GV chạy AI khối lượng lớn — Tiết kiệm 30%',
    description: '',
    features: [
      '≈ <strong>1.250 giáo án</strong> · <strong>830 slide</strong> · <strong>2.500 đề kiểm tra</strong> dùng linh hoạt (tặng thêm 5%).',
      'Nhãn <strong>Premium</strong> & Xóa dấu mộc.',
      'Ưu tiên xử lý AI tốc độ cao — không xếp hàng.',
      'Không mở thêm quyền nhóm hoặc chia credit.',
      'Không giới hạn thời gian sử dụng.'
    ]
  }
];

// =================================================================================
// CẤU HÌNH THƯỞNG VAI TRÒ
// =================================================================================
export const ROLE_BONUS_CONFIG = {
    ENABLE_BONUS: true,
    TEACHER: 50,
    PARENT: 30,
    STUDENT: 20,
};

// =================================================================================
// CẤU HÌNH THANH TOÁN
// =================================================================================
export const PAYMENT_CONFIG: any = {
    manualProofNoteRequired: false,
    methods: {
        bankTransfer: {
            enabled: true,
            bankName: 'MB BANK',
            accountNumber: '0988804286image.png',
            accountName: 'CONG TY GIAO DUC BOOKAI',
            qrCodeUrl: 'https://img.vietqr.io/image/mbbank-123456789-compact.jpg?amount={{amount}}&addInfo={{addInfo}}',
        },
        momo: {
            enabled: true,
            phone: '098804286',
            name: 'BOOKAI.VN',
        },
        payos: {
            enabled: false
        },
        vnpay: {
            enabled: false
        }
    }
};

/** Trùng tham số thứ hai của window.open — giữ qua redirect PayOS để biết đây là cửa sổ thanh toán */
export const PAYOS_POPUP_WINDOW_NAME = 'payos_checkout';
/** Khi COOP cắt window.opener, popup vẫn báo tab cha qua BroadcastChannel (cùng origin) */
export const PAYOS_RESULT_BROADCAST_CHANNEL = 'edu_bookai_payos_result';
/** Popup ghi localStorage → tab cha nhận sự kiện storage (ổn định khi postMessage/BroadcastChannel lỗi) */
export const PAYOS_RESULT_STORAGE_KEY = 'edu_payos_result_v1';

// =================================================================================
// CẤU HÌNH QUẢNG CÁO & TUÂN THỦ (Mặc định)
// =================================================================================
export const AD_CONFIG = {
    MIN_DISPLAY_TIME: 8000, 
    SHOW_ON_LOADING: true,
} as any;

// =================================================================================
// CẤU HÌNH UI & LABELS
// =================================================================================
export const UI_CONFIG: any = {
    CREDIT_LABEL: 'Tín dụng',
    APP_NAME: 'Bookai',
    APP_LOGO_URL: '/logo.png',
    APP_SUBTITLE: 'Đổi mới cách dạy và học với AI',
    SHOW_CREDIT_COSTS: false,
    // 'credits' (mặc định) = hiện số credit như hiện tại; 'plan' = ẩn số, hiện theo gói
    // (kiểu Claude/ChatGPT) — bật từ Admin → Cài đặt → AI → pricing_display_mode.
    PRICING_DISPLAY_MODE: 'credits' as 'credits' | 'plan',
    SHOW_APP_NAME_IN_HEADER: true,
    RECORDS_PER_PAGE: 10,
    DASHBOARD_GREETINGS: null,
    AFFILIATE_CASH_ENABLED: false,
};

export const FEATURE_FLAGS: any = {
    VIDEO_GENERATION: true,
    IMAGE_GENERATION: true,
    MUSIC_GENERATION: true,
    MUSIC_ENTERPRISE: false,
    MUSIC_LYRIA: true,
    SHARE_CREDITS: true,
    AUTO_ENABLE_ALL_QUICK_MENU: true,
    HISTORY_ASSISTANT: false,
    LITERATURE_ASSISTANT: false,
    SCIENCE_ASSISTANT: false,
};

export const EDU_CONFIG: any = {
    DEFAULT_LIMITS: {
        MIN_CREDITS_ONETIME_PURCHASE: 50,
        MAX_CLASSES: 3,
        MAX_STUDENTS: 50,
        MAX_CHILDREN: 2,
        MAX_LEARNING_PLANS: 3,
        /** Nhóm Bookai khi API chưa trả giá trị */
        MAX_GROUPS: 1,
    },
    GOOGLE_BIRTHDAY_SCOPE_ENABLED: false,
    STUDENT_DEFAULT_AGE_THRESHOLD: 18,
    MAX_PARENTS_PER_STUDENT: 2,
};

// =================================================================================
// CẤU HÌNH HOMEPAGE & DASHBOARD
// =================================================================================
export const HOMEPAGE_CONFIG: any = {
    HERO_TITLE: 'AI cho Giáo viên, Học sinh <br/> và Phụ huynh',
    HERO_SUBTITLE: 'Bookai giúp giáo viên tạo nội dung giảng dạy, trò chơi, video tương tác, mô phỏng khoa học, bài kiểm tra và quản lý lớp học online; đồng thời cá nhân hóa lộ trình học để học sinh tự học tốt hơn và phụ huynh theo sát tiến bộ của con.',
    /** Ảnh thay khối mockup «AI Dashboard» Hero; để trống = UI mẫu trong code */
    HERO_MOCKUP_IMAGE: '',
    HERO_BADGE_VALUE: '500k+',
    HERO_BADGE_LABEL: 'Học liệu đã tạo',
    VIDEO_PREVIEW_TITLE: 'Xem video giới thiệu',
    VIDEO_PREVIEW_SUBTITLE: 'Khám phá cách Bookai hỗ trợ đổi mới cách dạy và học',
    VIDEO_SOURCE: 'youtube', // 'server' hoặc 'youtube'
    YOUTUBE_VIDEO_ID: 'UIZAiXYceBI',
    VIDEO_URL: '',
    STATS: [
        { label: 'Giáo viên', value: '10,000+', color: 'text-teal-600', bg: 'bg-teal-50' },
        { label: 'Bài giảng', value: '500,000+', color: 'text-blue-600', bg: 'bg-blue-50' },
        { label: 'Học sinh', value: '100,000+', color: 'text-purple-600', bg: 'bg-purple-50' },
    ],
    PROMO_TITLE: 'Một nền tảng AI cho cả lớp học và gia đình',
    PROMO_DESCRIPTION: 'Giáo viên tạo học liệu và quản lý lớp nhanh hơn; học sinh học theo lộ trình cá nhân; phụ huynh nắm tiến bộ, bài tập và mục tiêu học tập của con.',
    TEACHER_SECTION_TITLE: 'Dành cho Giáo viên <br/> <span class="text-teal-600">Tạo bài giảng, kiểm tra và lớp học online</span>',
    TEACHER_SECTION_DESCRIPTION: 'Tạo nội dung giảng dạy trên lớp, giáo án 5E, slide, trò chơi, video tương tác học tập, mô phỏng khoa học, thí nghiệm trực quan, bài kiểm tra và quản lý lớp học online trong một nơi.',
    STUDENT_SECTION_TITLE: 'Dành cho Học sinh &amp; Phụ huynh <br/> <span class="text-blue-600">Tự học tốt hơn, đồng hành sát hơn</span>',
    STUDENT_SECTION_DESCRIPTION: 'Học sinh có lộ trình cá nhân hóa, luyện tiếng Anh, phát âm và sáng tạo; phụ huynh theo dõi tiến độ, bài tập, mục tiêu và hoạt động học hằng ngày của con.',
    FEATURE_SHOWCASE: [
        { title: 'Soạn giáo án AI - Tích hợp năng lực số', icon: 'DocumentTextIcon', description: 'Soạn giáo án 5E chuẩn GDPT 2018 (CV 5512), Tích hợp năng lực số theo Khung NLS của CV 3456/BGDĐT (QĐ 4793) và năng lực AI theo CV 3439.', media: [] },
        { title: 'Trò chơi & video tương tác', icon: 'PresentationChartBarIcon', description: 'Tạo game ôn tập, hoạt động sáng tạo và video bài giảng có điểm dừng tương tác để học sinh tham gia chủ động.', media: [] },
        { title: 'Bài kiểm tra & quản lý lớp', icon: 'QuestionMarkCircleIcon', description: 'Tạo đề chuẩn ma trận, giao bài, theo dõi kết quả và quản lý lớp học online trên cùng một hệ thống.', media: [] },
        { title: 'Mô phỏng khoa học & học cá nhân', icon: 'Square2StackIcon', description: 'Tạo mô phỏng 3D, thí nghiệm trực quan; đồng thời hỗ trợ học sinh luyện tập và phụ huynh theo dõi tiến bộ.', media: [] }
    ],
    TEACHER_TOOLS_MEDIA: {}, // Mapping of tool view to array of media strings
    STUDENT_TOOLS_MEDIA: {}, // Mapping of student tool view -> array of media URLs (Góc học tập)
    /** Dòng chứng thực dưới CTA trong slider GV / HS; avatar URLs — JSON [] = ảnh mẫu trong code */
    TEACHER_SOCIAL_CAPTION: '+5,000 Giáo viên tin dùng',
    TEACHER_SOCIAL_AVATARS: [] as string[],
    STUDENT_SOCIAL_CAPTION: '+10,000 Học sinh đang học',
    STUDENT_SOCIAL_AVATARS: [] as string[],
};

/** Popup / màn hình hướng dẫn onboarding — có thể ghi đè từ backend (`configService`). */
export const GUIDE_CONFIG: any = {
    VIDEO_SOURCE: 'youtube', // 'server' hoặc 'youtube'
    YOUTUBE_VIDEO_ID: '',
    VIDEO_URL: '',
    TITLE: 'Hướng dẫn sử dụng',
    CONTENT: '',
};

// =================================================================================
// CẤU HÌNH PHÁP LÝ & SEO
// =================================================================================
/**
 * Mặc định **rỗng** — khi backend chưa trả về content qua `configService`,
 * UI ở `TermsOfUse.tsx` / `PrivacyPolicy.tsx` sẽ tự rơi xuống nhánh
 * `DEFAULT_TERMS` / `DEFAULT_PRIVACY` (JSX inline đầy đủ) thay vì render
 * 1 chuỗi placeholder lập lờ qua `dangerouslySetInnerHTML`.
 */
export const LEGAL_CONFIG = {
    TERMS_OF_USE_HTML: '',
    PRIVACY_POLICY_HTML: '',
};

export const SEO_CONFIG: any = {
    DEFAULT_TITLE: 'Bookai.vn - Đổi mới cách dạy và học với AI',
    DEFAULT_DESCRIPTION:
        'Bookai là nền tảng AI cho giáo viên, học sinh và phụ huynh: tạo giáo án, trò chơi, video tương tác, mô phỏng khoa học, bài kiểm tra, quản lý lớp online và cá nhân hóa lộ trình học.',
    DEFAULT_KEYWORDS:
        'Bookai, AI giáo dục, đổi mới cách dạy và học, soạn giáo án AI, Tích hợp năng lực số, công văn 3456, khung năng lực số, tạo học liệu, bài tập AI, cá nhân hóa học tập, trợ lý giáo viên',
    PAGES: {
        '/': {
            title: 'Bookai.vn - Đổi mới cách dạy và học với AI',
            description:
                'Bookai hỗ trợ giáo viên tạo nội dung giảng dạy, trò chơi, video tương tác, mô phỏng khoa học, bài kiểm tra và quản lý lớp online; giúp học sinh học cá nhân hóa, luyện tiếng Anh; giúp phụ huynh theo dõi tiến bộ và đồng hành cùng con.',
            keywords: 'Bookai, AI giáo dục, giáo viên AI, tạo giáo án AI, trò chơi học tập, video tương tác học tập, mô phỏng khoa học, thí nghiệm trực quan, tạo bài kiểm tra, quản lý lớp học online, học sinh học cá nhân hóa, phụ huynh theo dõi học tập',
        },
        '/dang-nhap': {
            title: 'Đăng nhập',
            description: 'Đăng nhập Bookai để dùng trợ lý AI soạn giáo án, tạo học liệu và quản lý lớp học.',
            keywords: 'đăng nhập Bookai, tài khoản giáo viên',
        },
        '/chon-vai-tro': {
            title: 'Chọn vai trò',
            description: 'Chọn vai trò giáo viên, phụ huynh hoặc học sinh để cá nhân hóa trải nghiệm Bookai.',
            keywords: 'vai trò người dùng, giáo viên, học sinh, phụ huynh',
        },
        '/cong-cu-giao-vien': {
            title: 'Công cụ giáo viên',
            description: 'Bảng điều khiển công cụ dạy học: giáo án, slide, kiểm tra, mô phỏng, bài tập và hơn thế nữa.',
            keywords: 'công cụ giáo viên, dashboard giáo dục, Bookai',
        },
        '/thu-vien': {
            title: 'Thư viện của tôi',
            description: 'Quản lý giáo án, trình chiếu, bài kiểm tra, mô phỏng và tài nguyên đã lưu trên Bookai.',
            keywords: 'thư viện giáo viên, học liệu đã lưu, Bookai',
        },
        '/cai-dat': {
            title: 'Cài đặt tài khoản',
            description: 'Cập nhật hồ sơ, thông báo, giao diện và tùy chọn sử dụng Bookai.',
            keywords: 'cài đặt, tài khoản Bookai',
        },
        '/huong-dan': {
            title: 'Hướng dẫn sử dụng',
            description: 'Hướng dẫn chi tiết các tính năng Bookai: tạo học liệu AI, lớp học, credit và chia sẻ.',
            keywords: 'hướng dẫn Bookai, cách dùng',
        },
        '/gioi-thieu': {
            title: 'Giới thiệu',
            description: 'Tổng quan về Bookai — sứ mệnh và các công cụ hỗ trợ giáo dục bằng AI.',
            keywords: 'giới thiệu Bookai, về chúng tôi',
        },
        '/dieu-khoan': {
            title: 'Điều khoản sử dụng',
            description: 'Điều khoản và điều kiện sử dụng dịch vụ Bookai.vn.',
            keywords: 'điều khoản, terms of use, Bookai',
        },
        '/chinh-sach-bao-mat': {
            title: 'Chính sách bảo mật',
            description: 'Cách Bookai thu thập, lưu trữ và bảo vệ dữ liệu người dùng theo quy định.',
            keywords: 'bảo mật, privacy, Bookai',
        },
        '/tinh-nang': {
            title: 'Tính năng Bookai - Bộ công cụ AI cho dạy và học',
            description: 'Khám phá toàn bộ tính năng AI của Bookai: tạo giáo án 5E, đề kiểm tra chuẩn ma trận, cấu trúc TN THPT mới nhất, slide bài giảng, bài tập ôn luyện, mô phỏng STEM, phòng lab AI và hướng nghiệp.',
            keywords: 'tính năng Bookai, công cụ AI giáo dục, soạn giáo án AI, tạo đề kiểm tra, đề thi thử THPT 2025 2026, ma trận đề kiểm tra, đặc tả kỹ thuật, slide AI, hướng nghiệp AI, mô phỏng STEM, Bookai',
        },
        '/cho-giao-vien': {
            title: 'Bookai cho Giáo viên — Soạn giáo án, ra đề, quản lý lớp bằng AI',
            description: 'Bộ công cụ AI cho giáo viên: soạn giáo án 5E chuẩn CV 5512, tạo đề kiểm tra theo ma trận CV 7991, slide bài giảng, live quiz, chấm điểm và nhận xét học bạ theo TT 27/2020 & 22/2021. Dùng thử miễn phí.',
            keywords: 'phần mềm cho giáo viên, soạn giáo án AI, tạo đề kiểm tra, ma trận đề, giáo án 5E, công văn 5512, nhận xét học bạ AI, quản lý lớp học, live quiz, Bookai cho giáo viên',
        },
        '/cho-phu-huynh': {
            title: 'Bookai cho Phụ huynh — Theo dõi & đồng hành việc học của con',
            description: 'Theo dõi tiến độ, kết quả và điểm yếu của con theo thời gian thực, tạo bài luyện cá nhân hoá và định hướng nghề nghiệp, năng lực số cho con cùng Bookai. Miễn phí bắt đầu.',
            keywords: 'phần mềm cho phụ huynh, theo dõi việc học của con, báo cáo học tập, tạo bài tập cho con, hướng nghiệp cho con, năng lực số, Bookai cho phụ huynh',
        },
        '/cho-hoc-sinh': {
            title: 'Bookai cho Học sinh — Lộ trình học, luyện đề AI & khám phá nghề',
            description: 'Học đúng lộ trình cá nhân hoá mỗi ngày, luyện đề có AI chấm và chỉ ra lỗ hổng, gia sư AI 24/7, phòng lab năng lực số & AI và hành trình khám phá nghề nghiệp cùng Bookai.',
            keywords: 'ứng dụng học tập cho học sinh, lộ trình học cá nhân hoá, luyện đề online, gia sư AI, phòng lab AI, hướng nghiệp học sinh, năng lực số, Bookai cho học sinh',
        },
        '/cua-hang-credit': {
            title: 'Cửa hàng Credit',
            description: 'Mua gói credit Bookai để dùng AI tạo giáo án, slide, mô phỏng và các tính năng premium.',
            keywords: 'mua credit, gói Bookai, thanh toán',
        },
        '/lich-su-giao-dich': {
            title: 'Lịch sử giao dịch',
            description: 'Theo dõi lịch sử mua credit và giao dịch trên tài khoản Bookai.',
            keywords: 'lịch sử thanh toán, giao dịch credit',
        },
        '/thu-vien-cong-dong': {
            title: 'Thư viện cộng đồng',
            description: 'Khám phá học liệu chia sẻ bởi cộng đồng giáo viên trên Bookai.',
            keywords: 'thư viện cộng đồng, học liệu mở, chia sẻ giáo án',
        },
        '/chia-se': {
            title: 'Chia sẻ học liệu',
            description: 'Xem nội dung học liệu được chia sẻ qua liên kết Bookai.',
            keywords: 'chia sẻ, link học liệu, Bookai',
        },
        '/tham-gia-lop-hoc': {
            title: 'Tham gia lớp học',
            description: 'Nhập mã hoặc liên kết để tham gia lớp học trên Bookai.',
            keywords: 'tham gia lớp, mã lớp, Bookai',
        },
        '/tham-gia-lop': {
            title: 'Tham gia lớp',
            description: 'Hoàn tất tham gia lớp học với mã mời hoặc yêu cầu từ giáo viên.',
            keywords: 'join class, Bookai',
        },
        '/dac-quyen-premium': {
            title: 'Đặc quyền Premium',
            description: 'Lợi ích gói Premium Bookai: credit, lưu trữ, watermark và hỗ trợ ưu tiên.',
            keywords: 'Premium Bookai, đặc quyền',
        },
        '/affiliate-dashboard': {
            title: 'Affiliate',
            description: 'Bảng điều khiển chương trình giới thiệu và hoa hồng Bookai.',
            keywords: 'affiliate, giới thiệu Bookai',
        },
        '/sap-ra-mat': {
            title: 'Sắp ra mắt',
            description: 'Tính năng đang được phát triển trên Bookai — quay lại sau.',
            keywords: 'coming soon, Bookai',
        },
        '/hoc-ve': {
            title: 'Học vẽ',
            description: 'Studio vẽ và bài học minh họa với hỗ trợ AI trên Bookai.',
            keywords: 'học vẽ, drawing studio, Bookai',
        },
        '/luyen-phat-am': {
            title: 'Luyện phát âm',
            description: 'Cải thiện phát âm tiếng Anh với hướng dẫn IPA và phản hồi AI.',
            keywords: 'phát âm, pronunciation, Bookai',
        },
        '/ke-chuyen-lich-su': {
            title: 'Kể chuyện lịch sử',
            description: 'Khám phá lịch sử qua nội dung kể chuyện sinh động với AI.',
            keywords: 'lịch sử, storytelling, Bookai',
        },
        '/tro-ly-van-hoc': {
            title: 'Trợ lý văn học',
            description: 'Phân tích tác phẩm, tác giả và bối cảnh văn học với trợ lý AI.',
            keywords: 'văn học, trợ lý AI, Bookai',
        },
        '/giai-thich-khoa-hoc': {
            title: 'Giải thích khoa học',
            description: 'Làm rõ khái niệm khoa học phổ thông bằng ngôn ngữ dễ hiểu.',
            keywords: 'khoa học, STEM, giải thích, Bookai',
        },
        '/bang-dieu-khien-phu-huynh': {
            title: 'Bảng điều khiển phụ huynh',
            description: 'Theo dõi tiến độ học tập và thông báo của con trên Bookai.',
            keywords: 'phụ huynh, theo dõi học tập, Bookai',
        },
        '/lo-trinh-hoc-tap': {
            title: 'Lộ trình học tập',
            description: 'Lộ trình và bài học cá nhân hóa cho học sinh trên Bookai.',
            keywords: 'lộ trình học, học sinh, Bookai',
        },
        '/bang-hoc-cua-con': {
            title: 'Bảng học của con',
            description: 'Xem tiến độ, bài tập và hoạt động học tập của trẻ.',
            keywords: 'bảng học, phụ huynh, Bookai',
        },
        '/hanh-trinh-kham-pha': {
            title: 'Hành trình khám phá nghề nghiệp - AI định hướng cho con',
            description:
                'Định hướng nghề nghiệp cá nhân hoá bằng AI cho học sinh tiểu học, THCS, THPT và sinh viên: khám phá sở thích, đánh giá năng lực, gợi ý ngành phù hợp và xây lộ trình học tập rõ ràng.',
            keywords:
                'hướng nghiệp, định hướng nghề nghiệp, hướng nghiệp cho học sinh, hướng nghiệp THPT, hướng nghiệp THCS, hướng nghiệp tiểu học, AI hướng nghiệp, khám phá nghề nghiệp, lộ trình nghề nghiệp, trắc nghiệm nghề nghiệp, Bookai',
        },
        '/hanh-trinh-hoc-hang-ngay': {
            title: 'Hành trình học hằng ngày - Lộ trình AI cá nhân hoá',
            description:
                'Lớp học mỗi ngày 15 phút theo nghề mơ ước: AI giao bài cá nhân hoá, theo dõi streak, kỹ năng và tiến bộ của con từng ngày.',
            keywords:
                'lộ trình học hằng ngày, học mỗi ngày 15 phút, AI cá nhân hoá học tập, lớp học theo nghề, streak học tập, Bookai',
        },
        '/quan-ly-lop-hoc': {
            title: 'Quản lý lớp học',
            description: 'Tạo lớp, mời học sinh, giao bài và theo dõi kết quả trên Bookai.',
            keywords: 'quản lý lớp, lớp học online, Bookai',
        },
        '/bai-hoc': {
            title: 'Bài học',
            description: 'Nội dung module trong lộ trình học tập cá nhân hóa.',
            keywords: 'bài học, module học, Bookai',
        },
        '/tao-giao-an': {
            title: 'Tạo giáo án AI - Tích hợp năng lực số (CV 3456) | Bookai.vn',
            description: 'Soạn giáo án 5E chuẩn GDPT 2018 (CV 5512), Tích hợp năng lực số theo Khung NLS của Công văn 3456/BGDĐT-GDPT (Quyết định 4793/QĐ-BGDĐT) và năng lực AI theo CV 3439, có ngay mã thành phần (1.1.CB1, 2.6.CB1, 4.2.CB1...) trong từng hoạt động dạy học.',
            keywords: 'tạo giáo án AI, Tích hợp năng lực số, công văn 3456, khung năng lực số, QĐ 4793, soạn giáo án năng lực số, giáo án 5E, công văn 5512, công văn 3439, AI giáo viên, giáo án điện tử',
        },
        '/tao-de-kiem-tra': {
            title: 'Tạo đề kiểm tra AI - Ma trận & Cấu trúc THPT mới nhất',
            description: 'Thiết kế đề trắc nghiệm Đúng/Sai, Trả lời ngắn, 4 đáp án... chuẩn ma trận và bản đặc tả kỹ thuật của Bộ GD&ĐT (QĐ 764/QĐ-BGDĐT 2024).',
            keywords: 'tạo đề kiểm tra, đề thi thử THPT 2025, đề thi thử THPT 2026, ma trận đề kiểm tra, bản đặc tả kỹ thuật, trắc nghiệm đúng sai, trả lời ngắn, Bookai',
        },
        '/tao-tro-choi': {
            title: 'Tạo trò chơi giáo dục',
            description: 'Tạo trò chơi ôn tập tương tác cho lớp học.',
            keywords: 'trò chơi giáo dục, game học tập, Bookai',
        },
        '/tao-trinh-chieu': {
            title: 'Tạo trình chiếu',
            description: 'Chuyển tài liệu thành slide bài giảng chuyên nghiệp với AI.',
            keywords: 'slide, bài giảng, PowerPoint AI, Bookai',
        },
        '/tao-bai-tap': {
            title: 'Tạo bài tập & ôn tập AI - 10+ dạng bài đa dạng',
            description: 'Tạo phiếu bài tập về nhà, đề ôn tập với 10+ dạng bài tương tác (Đúng/Sai, Trả lời ngắn, Ghép cặp, Sắp xếp...). Tự động sinh từ tài liệu, có lời giải chi tiết.',
            keywords: 'tạo bài tập AI, phiếu ôn tập thông minh, trắc nghiệm đúng sai, trả lời ngắn, phiếu bài tập về nhà AI, Bookai',
        },
        '/tao-video': {
            title: 'Tạo video bài giảng',
            description: 'Tạo video bài giảng với MC ảo và giọng đọc từ nội dung của bạn.',
            keywords: 'video bài giảng, MC ảo, Bookai',
        },
        '/video-tuong-tac': {
            title: 'Video tương tác AI',
            description: 'Tạo và chia sẻ video bài giảng tương tác với câu hỏi trắc nghiệm, ghi chú và liên kết tài liệu tự động bằng AI.',
            keywords: 'video tương tác, AI video, câu hỏi trong video, Bookai, bài giảng tương tác',
        },
        '/tao-sang-kien-kinh-nghiem': {
            title: 'Sáng kiến kinh nghiệm',
            description: 'Soạn SKKN với cấu trúc và gợi ý nội dung từ AI.',
            keywords: 'SKKN, sáng kiến kinh nghiệm, Bookai',
        },
        '/hoat-dong-sang-tao': {
            title: 'Hoạt động sáng tạo',
            description: 'Thiết kế hoạt động dạy học sáng tạo: kịch bản, bài hát, câu chuyện.',
            keywords: 'hoạt động sáng tạo, HĐDH, Bookai',
        },
        '/phong-lab-ai': {
            title: 'Phòng Lab AI (NLc - CV 3439)',
            description: 'Trải nghiệm 4 module thí nghiệm AI: Perception, Learning, Interaction, Bias. Giáo viên tạo nội dung phù hợp cho từng đối tượng học sinh; giao diện tự thích ứng từ Tiểu học đến Đại học.',
            keywords: 'phòng lab AI, thí nghiệm AI, thực hành AI, NLc, CV 3439, Bookai, perception lab, learning lab, interaction lab, bias lab',
        },
        '/thu-thach-dao-duc-ai': {
            title: 'Thử thách Đạo đức AI (NLb - CV 3439)',
            description: 'Học tập và giải quyết các tình huống đạo đức khi sử dụng AI theo Công văn 3439.',
            keywords: 'đạo đức AI, NLb, CV 3439, Bookai',
        },
        '/kiem-chung-thong-tin-ai': {
            title: 'Kiểm chứng AI (NLa - CV 3439)',
            description: 'Rèn luyện tư duy phản biện và kỹ năng kiểm chứng thông tin từ AI theo Công văn 3439.',
            keywords: 'kiểm chứng AI, tư duy phản biện, NLa, CV 3439, Bookai',
        },
        '/slide-mau': {
            title: 'Slide mẫu',
            description: 'Khám phá và tùy biến mẫu slide bài giảng trên Bookai.',
            keywords: 'slide mẫu, template, Bookai',
        },
        '/soan-thao': {
            title: 'Soạn thảo tài liệu',
            description: 'Không gian soạn thảo và chỉnh sửa tài liệu giảng dạy.',
            keywords: 'soạn thảo, editor, Bookai',
        },
        '/thiet-ke': {
            title: 'Trình thiết kế',
            description: 'Poster, thẻ từ vựng, bìa bài giảng, phiếu bài tập, thiệp sinh nhật, bảng khen thưởng, bảng thực đơn, bảng phân công việc nhà với AI.',
            keywords: 'thiết kế giáo dục, poster,thẻ từ vựng, bìa bài giảng, phiếu bài tập, thiệp sinh nhật, bảng khen thưởng, bảng thực đơn, Bookai',
        },
        '/mo-phong-khoa-hoc/tao': {
            title: 'Tạo mô phỏng khoa học',
            description: 'Tạo mô phỏng tương tác: nhập chủ đề, AI sinh dữ liệu và bản xem chia sẻ.',
            keywords: 'mô phỏng khoa học, virtual lab, STEM, Bookai',
        },
        '/thiet-ke-3d/tao': {
            title: 'Tạo ứng dụng 3D',
            description:
                'Thiết kế mô hình 3D tương tác học tập: chủ đề, chú thích, mặt cắt và bài kiểm tra sinh động.',
            keywords: 'ứng dụng 3D giáo dục, mô hình 3D tương tác, học liệu 3D, Bookai',
        },
        '/thiet-ke-3d': {
            title: 'Ứng dụng 3D',
            description: 'Xem, chỉnh sửa và chia sẻ ứng dụng học tập 3D tương tác trên Bookai.',
            keywords: 'thiet ke 3D, học liệu 3D, Bookai',
        },
        '/mo-phong-khoa-hoc': {
            title: 'Mô phỏng khoa học',
            description: 'Xem, chỉnh sửa và chia sẻ mô phỏng khoa học tương tác.',
            keywords: 'mô phỏng khoa học, học STEM, Bookai',
        },
        '/kiem-tra-kien-thuc/tao': {
            title: 'Tạo kiểm tra kiến thức',
            description: 'Tạo bộ kiểm tra nhanh đầu giờ, giữa giờ hoặc cuối giờ với AI.',
            keywords: 'kiểm tra miệng, oral quiz, Bookai',
        },
        '/kiem-tra-kien-thuc/chay': {
            title: 'Chạy kiểm tra kiến thức',
            description: 'Tổ chức phiên kiểm tra trực tiếp trong lớp.',
            keywords: 'chạy kiểm tra, oral quiz, Bookai',
        },
        '/kiem-tra-kien-thuc/lich-su': {
            title: 'Lịch sử kiểm tra kiến thức',
            description: 'Xem lại các phiên kiểm tra miệng đã thực hiện và kết quả trên Bookai.',
            keywords: 'lịch sử kiểm tra miệng, oral quiz, Bookai',
        },
        '/kiem-tra-kien-thuc': {
            title: 'Kiểm tra kiến thức',
            description: 'Xem và chỉnh sửa nội dung kiểm tra kiến thức (oral quiz).',
            keywords: 'kiểm tra kiến thức, Bookai',
        },
        '/giao-an': {
            title: 'Giáo án đã lưu',
            description: 'Mở và chỉnh sửa giáo án đã lưu trong thư viện.',
            keywords: 'giáo án, xem giáo án, Bookai',
        },
        '/de-kiem-tra': {
            title: 'Đề kiểm tra',
            description: 'Xem và chỉnh sửa đề kiểm tra đã lưu.',
            keywords: 'đề kiểm tra, quiz, Bookai',
        },
        '/tro-choi': {
            title: 'Trò chơi giáo dục',
            description: 'Chơi và chỉnh sửa trò chơi ôn tập đã lưu.',
            keywords: 'trò chơi giáo dục, Bookai',
        },
        '/trinh-chieu': {
            title: 'Trình chiếu',
            description: 'Xem và chỉnh sửa bài trình chiếu đã lưu.',
            keywords: 'trình chiếu, slide, Bookai',
        },
        '/kho-bai-tap': {
            title: 'Bài tập',
            description: 'Xem và chỉnh sửa bài tập về nhà đã lưu.',
            keywords: 'bài tập, homework, Bookai',
        },
        '/kho-hoat-dong': {
            title: 'Hoạt động dạy học',
            description: 'Xem và chỉnh sửa hoạt động sáng tạo đã lưu.',
            keywords: 'hoạt động dạy học, Bookai',
        },
        '/bai-hoc-ve': {
            title: 'Bài học vẽ',
            description: 'Xem và chỉnh sửa bài học vẽ đã lưu.',
            keywords: 'bài học vẽ, Bookai',
        },
    },
};

/**
 * Bản chụp SEO từ mã nguồn — dùng làm fallback trước khi API tải và merge với cấu hình admin.
 * Giá trị rỗng trên DB được hiểu là “giữ mặc định code”.
 */
export const SEO_STATIC_SNAPSHOT: {
    DEFAULT_TITLE: string;
    DEFAULT_DESCRIPTION: string;
    DEFAULT_KEYWORDS: string;
    PAGES: Record<string, { title?: string; description?: string; keywords?: string }>;
} = {
    DEFAULT_TITLE: SEO_CONFIG.DEFAULT_TITLE,
    DEFAULT_DESCRIPTION: SEO_CONFIG.DEFAULT_DESCRIPTION,
    DEFAULT_KEYWORDS: SEO_CONFIG.DEFAULT_KEYWORDS,
    PAGES: JSON.parse(JSON.stringify(SEO_CONFIG.PAGES)),
};

// =================================================================================
// CẤU HÌNH THÔNG TIN LIÊN HỆ
// =================================================================================
export const CONTACT_CONFIG: any = {
    EMAIL: 'bookai.vn@gmail.com',
    HOTLINE: '0988804286',
    ADDRESS: 'Hà Nội, Việt Nam',
    ZALO_LINK: 'https://zalo.me/0988804286',
    FACEBOOK_LINK: 'https://facebook.com/bookai.vn',
    MESSENGER_LINK: 'https://m.me/bookai.vn',
    YOUTUBE_LINK: 'https://youtube.com/@bookai_vn',
    TIKTOK_LINK: 'https://tiktok.com/@bookai.vn',
};

// =================================================================================
// CẤU HÌNH CHÂN TRANG (FOOTER)
// =================================================================================
export const FOOTER_CONFIG: any = {
    CREATIVE_TITLE: 'Sáng tạo',
    CREATIVE_LINKS: [
        { label: 'Soạn giáo án AI - Tích hợp năng lực số', view: 'LessonPlanCreator', role: 'teacher' },
        { label: 'Tạo trình chiếu', view: 'SlideCreator', role: 'teacher' },
        { label: 'Thiết kế ứng dụng 3D', view: 'ThietKe3DCreator', role: 'teacher' },
        { label: 'Mô phỏng khoa học', view: 'ScientificSimulationCreator', role: 'teacher' },
        { label: 'Soạn hoạt động', view: 'ActivityCreator', role: 'teacher' },
        { label: 'Tạo trò chơi', view: 'GameCreator', role: 'teacher' },
    ],
    LEARNING_TITLE: 'Học tập',
    LEARNING_LINKS: [
        { label: 'Thư viện cộng đồng', view: 'SharedLibrary', role: 'all' },
        { label: 'Luyện phát âm', view: 'Pronunciation', role: 'all' },
        { label: 'Bảng giá Credit', view: 'CreditStore', role: 'standard' },
    ],
    SUPPORT_TITLE: 'Hỗ trợ & Pháp lý',
    SUPPORT_LINKS: [
        { label: 'Khám phá tính năng', view: '/tinh-nang', role: 'all' },
        { label: 'Bookai cho giáo viên', view: '/cho-giao-vien', role: 'all' },
        { label: 'Bookai cho phụ huynh', view: '/cho-phu-huynh', role: 'all' },
        { label: 'Bookai cho học sinh', view: '/cho-hoc-sinh', role: 'all' },
        { label: 'Điều khoản dịch vụ', view: 'TermsOfUse', role: 'all' },
        { label: 'Chính sách bảo mật', view: 'PrivacyPolicy', role: 'all' },
        { label: 'Hướng dẫn sử dụng', view: 'ComingSoon', role: 'all' },
    ],
    DESCRIPTION: 'Bookai là ứng dụng AI hỗ trợ giáo viên, phụ huynh và học sinh đổi mới cách dạy và học theo hướng hiệu quả, trực quan và dễ áp dụng.',
    TAX_INFO: 'Mã số thuế: 0101234567 • Cấp bởi Sở KH&ĐT Hà Nội',
    COPYRIGHT: '© 2026 Bookai.vn. All rights reserved.',
    CRAFTED_BY: 'Crafted by Bookai Team',
};

// =================================================================================
// ĐƯỜNG DẪN TÀI NGUYÊN (ASSETS)
// =================================================================================
export const ASSET_PATHS = {
    GAME_TEMPLATES: {
        [GameType.TIM_MAU_TIM_HINH]: 'games/mam-non/tim-mau-hinh.html',
        [GameType.BE_BAT_CHUOC]: 'games/mam-non/be-bat-chuoc.html',
        [GameType.GHEP_HINH_GHEP_CHU]: 'games/mam-non/ghep-hinh-chu.html',
        [GameType.VONG_QUAY_AM_NHAC]: 'games/mam-non/vong-quay-nhac.html',
        [GameType.AI_NHANH_HON]: 'games/tieu-hoc/ai-nhanh-hon.html',
        [GameType.O_CHU_BI_MAT]: 'games/tieu-hoc/o-chu-bi-mat.html',
        [GameType.TRUY_TIM_KHO_BAU]: 'games/tieu-hoc/truy-tim-kho-bau.html',
        [GameType.GHEP_DOI_THONG_TIN]: 'games/tieu-hoc/ghep-doi-thong-tin.html',
        [GameType.AI_LA_TRIEU_PHU_MINI]: 'games/thcs/ai-la-trieu-phu.html',
        [GameType.DONG_VAI_TINH_HUONG]: 'games/thcs/dong-vai.html',
        [GameType.TIEP_SUC]: 'games/thcs/tiep-suc.html',
        [GameType.DO_VUI_LINH_HOAT]: 'games/thcs/do-vui.html',
        [GameType.TRANH_BIEN_NHANH]: 'games/thpt/tranh-bien.html',
        [GameType.GIAI_MA_O_CHU]: 'games/thpt/giai-ma-o-chu.html',
        [GameType.CASE_STUDY_NHO]: 'games/thpt/case-study.html',
        [GameType.AI_LA_NHA_KHOA_HOC_TRE]: 'games/thpt/nha-khoa-hoc-tre.html',
        [GameType.BINGO_KIEN_THUC]: 'games/common/bingo.html',
    }
};
